import React from 'react';
import PropTypes from 'prop-types';
import BRAND_LOGOS from '../assets/logos';
import './Preloader.css';

/**
 * Preloader — indeterminate loading line.
 * A dark segment slides over a faint track: short on the left, widest in the
 * middle, short again toward the right, looping. Used in place of "Loading…"
 * text on Product / SchoolShop / MenuItemShop pages.
 *
 * Props:
 *  - label     — accessible label (aria-label / SR text). Default "Loading".
 *  - variant   — 'default' (line only) | 'logo' (brand logotype above the line).
 *  - brand     — 'CLS' | 'ABC' | 'Overall' | 'Crownstudent'; picks the logotype for `variant="logo"`.
 *  - logo      — a logotype URL (e.g. from a CMS): shown above the line whatever
 *                the brand — the data decides, `variant` is then implied.
 *  - className — extra class on the root.
 */
const Preloader = ({ label = 'Loading', variant = 'default', brand = 'CLS', logo, className = '' }) => {
  const logoBrand = BRAND_LOGOS[brand] ? brand : 'CLS';
  const logoSrc = logo || (variant === 'logo' ? BRAND_LOGOS[logoBrand].main : null);

  return (
    <div
      className={`ds-preloader ${logoSrc ? `ds-preloader--logo ds-preloader--${logoBrand.toLowerCase()}` : ''} ${className}`.replace(/\s+/g, ' ').trim()}
      role="status"
      aria-label={label}
    >
      {logoSrc && (
        <img src={logoSrc} alt="" aria-hidden="true" className="ds-preloader__logo" />
      )}
      <div className="ds-preloader__track">
        <div className="ds-preloader__bar" />
      </div>
    </div>
  );
};

Preloader.propTypes = {
  /** Accessible name only — aria-label on the role="status" root */
  label: PropTypes.string,
  /** 'logo' stacks the brand logotype above the line */
  variant: PropTypes.oneOf(['default', 'logo']),
  /** Brand logotype used by variant="logo" (unknown → CLS) */
  brand: PropTypes.oneOf(['CLS', 'ABC', 'Overall', 'Crownstudent']),
  /** Logotype URL shown above the line, whatever the brand (implies the logo variant) */
  logo: PropTypes.string,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default Preloader;
